// src/pages/ProfilePage.js
import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../AuthContext';
import { firestore, doc, getDoc, collection, onSnapshot, getDocs } from '../firebase-config';
import ColorStatsPie from '../components/ColorStatsPie';
import './ProfilePage.css';

const ProfilePage = () => {
  const { playerId } = useParams();
  const { verifiedPlayer } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [playerData, setPlayerData] = useState(null);
  const [colorStats, setColorStats] = useState(null);
  const [paintedBlocks, setPaintedBlocks] = useState([]);
  const [ownedBlocks, setOwnedBlocks] = useState([]);
  const [totalPaintedRank, setTotalPaintedRank] = useState(null);
  const [blockCoverageRank, setBlockCoverageRank] = useState(null);
  const [coveragePercentage, setCoveragePercentage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadPlayerProfile();
  }, [playerId]);

  const calculateCoveragePercentage = async (ownedBlocksCount) => {
  try {
    // Get total unique blocks on the canvas (not total paint actions)
    const blocksSnapshot = await getDocs(collection(firestore, 'blocks'));
    const totalCanvasBlocks = blocksSnapshot.size;
    
    if (totalCanvasBlocks > 0 && ownedBlocksCount > 0) {
      const percentage = (ownedBlocksCount / totalCanvasBlocks) * 100;
      setCoveragePercentage(percentage);
    } else {
      setCoveragePercentage(0);
    }
  } catch (error) {
    console.error('Error calculating coverage percentage:', error);
    setCoveragePercentage(null);
  }
};

  const loadPlayerProfile = async () => {
    try {
      setLoading(true);
      setError(null);

      const playerIdDoc = await getDoc(doc(firestore, 'playerIds', playerId));
      
      if (!playerIdDoc.exists()) {
        setError('Player not found');
        setLoading(false);
        return;
      }

      const uuid = playerIdDoc.data().uuid;

      const playerRef = doc(firestore, 'players', uuid);
      const unsubscribePlayer = onSnapshot(playerRef, (playerDoc) => {
        if (!playerDoc.exists()) {
          setError('Player data not found');
          setLoading(false);
          return;
        }

        const data = playerDoc.data();
        setPlayerData({ uuid, ...data });
        setLoading(false);
      });

      const colorStatsRef = doc(firestore, 'players', uuid, 'colourStats', 'stats');
      const unsubscribeColorStats = onSnapshot(colorStatsRef, (colorStatsDoc) => {
        if (colorStatsDoc.exists()) {
          setColorStats(colorStatsDoc.data());
        }
      });

      const paintedBlocksRef = collection(firestore, 'players', uuid, 'paintedBlocks');
      const unsubscribePaintedBlocks = onSnapshot(paintedBlocksRef, (snapshot) => {
        const blocks = [];
        snapshot.forEach((doc) => {
          blocks.push({ id: doc.id, ...doc.data() });
        });
        blocks.sort((a, b) => b.timestamp - a.timestamp);
        setPaintedBlocks(blocks);
      });

      const ownedBlocksRef = collection(firestore, 'players', uuid, 'ownedBlocks');
      const unsubscribeOwnedBlocks = onSnapshot(ownedBlocksRef, (snapshot) => {
        const blocks = [];
        snapshot.forEach((doc) => {
          blocks.push({ id: doc.id, ...doc.data() });
        });
        blocks.sort((a, b) => b.timestamp - a.timestamp);
        setOwnedBlocks(blocks);

        if (blocks.length > 0) {
          calculateCoveragePercentage(blocks.length);
        } else {
          setCoveragePercentage(0);
        }
      });

      calculateRanks(uuid);

      return () => {
        unsubscribePlayer();
        unsubscribeColorStats();
        unsubscribePaintedBlocks();
        unsubscribeOwnedBlocks();
      };

    } catch (err) {
      console.error('Error loading profile:', err);
      setError('Failed to load profile');
      setLoading(false);
    }
  };

  const calculateRanks = async (uuid) => {
    try {
      const playersSnapshot = await getDocs(collection(firestore, 'players'));
      const players = [];

      for (const playerDoc of playersSnapshot.docs) {
        const data = playerDoc.data();
        
        const ownedBlocksSnapshot = await getDocs(
          collection(firestore, 'players', playerDoc.id, 'ownedBlocks')
        );

        players.push({
          uuid: playerDoc.id,
          blocksPainted: data.blocksPainted || 0,
          ownedBlocksCount: ownedBlocksSnapshot.size
        });
      }

      const sortedByTotal = [...players].sort((a, b) => b.blocksPainted - a.blocksPainted);
      const totalRank = sortedByTotal.findIndex(p => p.uuid === uuid) + 1;
      setTotalPaintedRank(totalRank);

      const sortedByOwned = [...players].sort((a, b) => b.ownedBlocksCount - a.ownedBlocksCount);
      const ownedRank = sortedByOwned.findIndex(p => p.uuid === uuid) + 1;
      setBlockCoverageRank(ownedRank);

    } catch (error) {
      console.error('Error calculating ranks:', error);
    }
  };

  const getRankStyle = (rank) => {
    if (rank === 1) {
      return { color: '#FFD700', emoji: '🏆' };
    } else if (rank === 2) {
      return { color: '#C0C0C0', emoji: '🥈' };
    } else if (rank === 3) {
      return { color: '#CD7F32', emoji: '🥉' };
    } else if (rank <= 10) {
      return { color: '#5dade2', emoji: '⭐' };
    } else {
      return { color: '#aaaaaa', emoji: '#' };
    }
  };

  const handleRankClick = (type) => {
    if (type === 'total') {
      navigate('/leaderboards/player/total-painted');
    } else {
      navigate('/leaderboards/player/block-coverage');
    }
  };

  const convertMinecraftColor = (code) => {
    const colorMap = {
      '&0': '#000000', '&1': '#0000AA', '&2': '#00AA00', '&3': '#00AAAA',
      '&4': '#AA0000', '&5': '#AA00AA', '&6': '#FFAA00', '&7': '#AAAAAA',
      '&8': '#555555', '&9': '#5555FF', '&a': '#55FF55', '&b': '#55FFFF',
      '&c': '#FF5555', '&d': '#FF55FF', '&e': '#FFFF55', '&f': '#FFFFFF',
      '§0': '#000000', '§1': '#0000AA', '§2': '#00AA00', '§3': '#00AAAA',
      '§4': '#AA0000', '§5': '#AA00AA', '§6': '#FFAA00', '§7': '#AAAAAA',
      '§8': '#555555', '§9': '#5555FF', '§a': '#55FF55', '§b': '#55FFFF',
      '§c': '#FF5555', '§d': '#FF55FF', '§e': '#FFFF55', '§f': '#FFFFFF'
    };
    return colorMap[code] || '#FFFFFF';
  };

  const stripColorCodes = (text) => {
    if (!text) return '';
    return text.replace(/[§&$][0-9a-fk-or]/gi, '');
  };

  if (loading) {
    return (
      <div className="container profile-page">
        <div className="loading">Loading profile...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container profile-page">
        <div className="card error-card">
          <h2>{error}</h2>
          <button onClick={() => navigate('/')}>Go Home</button>
        </div>
      </div>
    );
  }

  const isOwnProfile = verifiedPlayer && verifiedPlayer.uuid === playerData.uuid;
  const totalRankStyle = totalPaintedRank ? getRankStyle(totalPaintedRank) : null;
  const coverageRankStyle = blockCoverageRank ? getRankStyle(blockCoverageRank) : null;

  return (
    <div className="container profile-page">
      <div className="profile-header card">
        <img 
          src={`https://mc-heads.net/body/${playerData.uuid}/190`}
          alt={playerData.username}
          className="player-render"
          onError={(e) => {
            e.target.src = `https://crafatar.com/avatars/${playerData.uuid}?size=190&overlay`;
          }}
        />
        <div className="profile-info">
          <div className="profile-title">
            {playerData.rankPrefix && (
              <span className="rank-prefix">{stripColorCodes(playerData.rankPrefix)}</span>
            )}
            {playerData.icon && (
              <span 
                className="player-icon"
                style={{ color: convertMinecraftColor(playerData.icon.substring(0, 2)) }}
              >
                {stripColorCodes(playerData.icon)}
              </span>
            )}
            <h1 style={{ color: convertMinecraftColor(playerData.nameColour) }}>
              {playerData.username}
            </h1>
          </div>
          <p className="player-id">Player #{playerData.playerId}</p>
          
          <div className="rank-badges">
            {totalPaintedRank && (
              <div 
                className="rank-badge-item clickable"
                onClick={() => handleRankClick('total')}
                title="Click to view leaderboard"
              >
                <span className="rank-emoji">{totalRankStyle.emoji}</span>
                <span className="rank-text" style={{ color: totalRankStyle.color }}>
                  #{totalPaintedRank}
                </span>
                <span className="rank-label">Total Blocks</span>
              </div>
            )}
            {blockCoverageRank && (
              <div 
                className="rank-badge-item clickable"
                onClick={() => handleRankClick('coverage')}
                title="Click to view leaderboard"
              >
                <span className="rank-emoji">{coverageRankStyle.emoji}</span>
                <span className="rank-text" style={{ color: coverageRankStyle.color }}>
                  #{blockCoverageRank}
                </span>
                <span className="rank-label">Block Coverage</span>
              </div>
            )}
          </div>

          {isOwnProfile && <span className="own-profile-badge">Your Profile</span>}
        </div>
      </div>

      <div className="stats-grid">
        <div className="card stat-card" title="Total paint actions - every time you paint a block, even if painting over the same spot">
          <div className="stat-value">{playerData.blocksPainted?.toLocaleString() || 0}</div>
          <div className="stat-label">Total Blocks</div>
        </div>
        
        <div className="card stat-card" title="Coordinates where you're still the current painter - blocks you own that haven't been painted over">
          <div className="stat-value">
            {ownedBlocks.length.toLocaleString()}
            {coveragePercentage !== null && (
              <span className="stat-percentage">
                ({coveragePercentage < 0.01 ? '<0.01' : coveragePercentage.toFixed(2)}%)
              </span>
            )}
          </div>
          <div className="stat-label">Block Coverage</div>
        </div>
        
        <div className="card stat-card" title="Most recent time you were active on the server">
          <div className="stat-value">
            {playerData.lastActive ? new Date(playerData.lastActive).toLocaleDateString() : 'Unknown'}
          </div>
          <div className="stat-label">Last Active</div>
        </div>

        <div className="card stat-card" title="The date you first joined the server.">
          <div className="stat-value">
            {playerData.firstJoinDate ? new Date(playerData.firstJoinDate).toLocaleDateString() : 'Unknown'}
          </div>
          <div className="stat-label">Join Date</div>
        </div>
      </div>

      {playerData && (
        <div className="color-stats-dual-section">
          <ColorStatsPie 
            playerData={{ uuid: playerData.uuid, username: playerData.username }} 
            mode="total"
            title="Total Blocks"
          />
          <ColorStatsPie 
            playerData={{ uuid: playerData.uuid, username: playerData.username }} 
            mode="coverage"
            title="Block Coverage"
          />
        </div>
      )}
    </div>
  );
};

export default ProfilePage;